// src/components/FactPlan.jsx
import { memo, useLayoutEffect, useRef } from "react";

/**
 * Компактный блок «факт / план ч» с прогресс-баром.
 *
 * Единая точка отображения часов для карточки проекта (ProjectProgress)
 * и карточки задачи (TaskCard в канбане и TaskCardRow в списке). Формат -
 * «Факт / План ч» (факт первым: это выполненное, взгляд идёт туда в
 * первую очередь). Перерасход (факт > план) выделяет факт классом .red -
 * как в карточке проекта.
 *
 * Ширина и цвет заливки - через CSS-переменные --pj-progress-fill и
 * --pj-progress-color, выставленные императивно в useLayoutEffect
 * (паттерн TaskProgress / WorkloadBar / PriorityStrip).
 *
 * Цвет приходит сверху: вызывающий решает, «чей» это цвет. Для проекта
 * это цвет приоритета проекта, для задачи - цвет приоритета задачи.
 * Сам компонент про это правило ничего не знает - I из SOLID.
 *
 * memo: рендерится по одному на карточку; на больших списках это
 * десятки узлов. Пропсы - примитивы (fact, plan, color), shallow-compare
 * работает по значению.
 */
export const FactPlan = memo(function FactPlan({ fact, plan, color, title }) {
  const barRef = useRef(null);
  const hasBar = plan != null && plan > 0;
  const over = hasBar && fact > plan;
  const pct = hasBar ? Math.min(100, (fact / plan) * 100) : 0;

  useLayoutEffect(() => {
    if (!barRef.current || !hasBar) return;
    barRef.current.style.setProperty("--pj-progress-fill", `${pct}%`);
    barRef.current.style.setProperty("--pj-progress-color", color || "");
  }, [pct, color, hasBar]);

  const tooltip =
    title ?? (hasBar ? `Факт: ${fact} ч, План: ${plan} ч` : `Факт: ${fact} ч`);

  return (
    <div className="pj-plan" title={tooltip}>
      <div className="pj-plan-row">
        {hasBar ? (
          <span>
            <b className={over ? "red" : ""}>{fact}</b>
            {" / "}
            <b>{plan}</b> ч
          </span>
        ) : (
          <span>
            <b>{fact}</b> ч
          </span>
        )}
      </div>
      {hasBar && (
        <div className="pj-progress">
          <div ref={barRef} className="pj-progress-fill" />
        </div>
      )}
    </div>
  );
});

export default FactPlan;
